import React, { useEffect, useRef, useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { cn } from '../../lib/utils';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  snapPoints?: number[]; // Array of heights as percentages (0-1)
  defaultSnap?: number; // Index of default snap point
  dismissible?: boolean;
  showHandle?: boolean;
}

interface BottomSheetHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
  showHandle?: boolean;
}

interface BottomSheetBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface BottomSheetFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function BottomSheet({
  isOpen,
  onClose,
  children,
  className,
  snapPoints = [0.5, 0.9],
  defaultSnap = 1,
  dismissible = true,
  showHandle = true,
}: BottomSheetProps) {
  const [currentSnap, setCurrentSnap] = useState(defaultSnap);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, dismissible]);

  // Reset snap point when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentSnap(defaultSnap);
      setCurrentY(0);
    }
  }, [isOpen, defaultSnap]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!showHandle) return;
      setIsDragging(true);
      setStartY(e.touches[0].clientY);
      setCurrentY(0);
    },
    [showHandle]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const deltaY = e.touches[0].clientY - startY;
      setCurrentY(deltaY);
    },
    [isDragging, startY]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 100; // Minimum drag distance to trigger snap change

    if (currentY > threshold) {
      // Dragging down
      if (currentSnap > 0) {
        setCurrentSnap(currentSnap - 1);
      } else if (dismissible) {
        onClose();
      }
    } else if (currentY < -threshold) {
      // Dragging up
      if (currentSnap < snapPoints.length - 1) {
        setCurrentSnap(currentSnap + 1);
      }
    }

    setCurrentY(0);
  }, [isDragging, currentY, currentSnap, snapPoints.length, dismissible, onClose]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!showHandle) return;
      setIsDragging(true);
      setStartY(e.clientY);
      setCurrentY(0);
    },
    [showHandle]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaY = e.clientY - startY;
      setCurrentY(deltaY);
    },
    [isDragging, startY]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 100;

    if (currentY > threshold) {
      if (currentSnap > 0) {
        setCurrentSnap(currentSnap - 1);
      } else if (dismissible) {
        onClose();
      }
    } else if (currentY < -threshold) {
      if (currentSnap < snapPoints.length - 1) {
        setCurrentSnap(currentSnap + 1);
      }
    }

    setCurrentY(0);
  }, [isDragging, currentY, currentSnap, snapPoints.length, dismissible, onClose]);

  // Mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!isOpen) return null;

  const heightPercentage = snapPoints[currentSnap] * 100;
  const dragOffset = isDragging ? Math.max(0, currentY) : 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-30 z-40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0'
        )}
        onClick={dismissible ? onClose : undefined}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={cn(
          'fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border transition-transform duration-300 ease-out',
          isDragging ? 'transition-none' : '',
          className
        )}
        style={{
          height: `${heightPercentage}%`,
          transform: `translateY(${dragOffset}px)`,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 bg-muted-foreground/30 rounded-full cursor-grab active:cursor-grabbing" />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col h-full overflow-hidden">{children}</div>
      </div>
    </>
  );
}

export function BottomSheetHeader({
  children,
  onClose,
  className,
  showHandle = false,
}: BottomSheetHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-4 pb-2 flex-shrink-0',
        !showHandle && 'pt-6',
        className
      )}
    >
      <div className="flex-1">{children}</div>
      {onClose && (
        <Button
          variant="ghost"
          onClick={onClose}
          className="rounded-md text-muted-foreground hover:text-foreground ml-4 min-h-[44px] min-w-[44px] p-2"
        >
          <span className="sr-only">Close</span>
          <XMarkIcon className="h-6 w-6" />
        </Button>
      )}
    </div>
  );
}

export function BottomSheetBody({ children, className }: BottomSheetBodyProps) {
  return <div className={cn('flex-1 px-4 py-2 overflow-y-auto', className)}>{children}</div>;
}

export function BottomSheetFooter({ children, className }: BottomSheetFooterProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-3 p-4 pt-2 border-t border-border flex-shrink-0 bg-card',
        className
      )}
    >
      {children}
    </div>
  );
}
